import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PublicApiService } from '../../services/public-api.service';
import { Fleet } from "../../models/fleet.model";
import { Location } from "../../models/location.model";

// Extended Fleet interface with level information for hierarchical display
interface FleetWithLevel extends Fleet {
  level: number;
}

declare var bootstrap: any;

@Component({
  selector: 'app-change-fleet',
  templateUrl: './change-fleet.component.html',
  styleUrls: ['./change-fleet.component.scss']
})
export class ChangeFleetComponent implements OnInit {
  @Input() selectedLocation: Location | null = null;
  @Output() fleetChanged = new EventEmitter<void>();

  fleets: Fleet[] = [];
  flattenedFleets: FleetWithLevel[] = [];
  selectedFleet: Fleet | null = null;
  loading = false;
  error = '';

  constructor(private publicApiService: PublicApiService) {}

  ngOnInit(): void {}

  openChangeFleetModal(selectedLocation: Location | null) {
    // Reset the component state
    this.selectedLocation = selectedLocation;
    this.selectedFleet = null;
    this.loading = false;
    this.error = '';

    // Load the fleets
    this.loadFleets();

    // Get the modal element
    const modalElement = document.getElementById('changeFleetModal');

    // Create and show the modal
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  loadFleets(): void {
    this.loading = true;
    this.error = '';

    this.publicApiService.getUserFleets().subscribe({
      next: (response) => {
        // Check if response has a data property
        if (response.data) {
          this.fleets = response.data;
        } else {
          // If response is an array, use it directly
          this.fleets = Array.isArray(response) ? response : [];
        }

        // Flatten the hierarchical structure of fleets for display in the dropdown
        this.flattenedFleets = this.flattenFleets(this.fleets);
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load fleets. Please try again.';
        this.loading = false;
        console.error('Error loading fleets:', err);
      }
    });
  }

  flattenFleets(fleets: Fleet[]): FleetWithLevel[] {
    const result: FleetWithLevel[] = [];
    const processFleet = (fleet: Fleet, level: number) => {
      // Add the fleet with its level to the result
      result.push({
        ...fleet,
        level
      });

      // Process children with an increased level
      if (fleet.children && fleet.children.length > 0) {
        fleet.children.forEach(child => processFleet(child, level + 1));
      }
    };

    fleets.forEach(fleet => processFleet(fleet, 0));
    return result;
  }

  selectFleet(fleet: Fleet): void {
    this.selectedFleet = fleet;
  }

  closeModal(): void {
    // Close the modal
    const modalElement = document.getElementById('changeFleetModal');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    // Reset the component state
    this.selectedFleet = null;
    this.error = '';
  }

  async changeFleet(): Promise<void> {
    if (!this.selectedLocation || !this.selectedFleet) {
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      await this.publicApiService.updateLocationFleet(this.selectedLocation.uuid, this.selectedFleet.id);

      // Emit the fleetChanged event to notify parent components
      this.fleetChanged.emit();

      // Close the modal
      this.closeModal();
    } catch (error) {
      console.error('Error changing fleet:', error);
      this.error = 'Failed to change fleet. Please try again.';
      this.loading = false;
    }
  }
}
